import { useState } from 'react';
import { CalendarDays, Flag, Plus, Timer, Trash2 } from 'lucide-react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { MultiSelect } from '@/shared/components/ui';
import { cn } from '@/shared/utils/utils';
import {
  DEPT_LABELS,
  PRIORITY_OPTIONS,
  PRIORITY_TONE,
  STATUS_LABEL,
  STATUS_OPTIONS,
  STATUS_TONE,
  deadlineInfo,
  progressPct,
} from '../constants';
import { getAssignedIds } from '../types';
import type {
  Project,
  ProjectMilestone,
  ProjectPriority,
  ProjectSprint,
  ProjectStatus,
  UpdateProjectPayload,
  UserOption,
} from '../types';

interface ProjectDrawerProps {
  project: Project | null;
  users: UserOption[];
  /** Manager+ may edit; employees see a read-only view. */
  canEdit: boolean;
  saving?: boolean;
  onClose: () => void;
  onSave: (id: string, payload: UpdateProjectPayload) => void;
}

type DrawerForm = {
  status: ProjectStatus;
  priority: ProjectPriority;
  assigned_to: string[];
  deadline: string;
  description: string;
  milestones: ProjectMilestone[];
  sprints: ProjectSprint[];
};

function toForm(project: Project): DrawerForm {
  return {
    status: project.status ?? 'active',
    priority: project.priority ?? 'medium',
    assigned_to: getAssignedIds(project.assigned_to),
    deadline: project.deadline ?? '',
    description: project.description ?? '',
    milestones: (project.milestones ?? []).map((m) => ({ ...m })),
    sprints: (project.sprints ?? []).map((s) => ({ ...s })),
  };
}

let localIdCounter = 0;
function localId(): string {
  localIdCounter += 1;
  return `local-${Date.now()}-${localIdCounter}`;
}

function formatValue(value?: number): string {
  if (!value) return '—';
  return `₹${value.toLocaleString('en-IN')}`;
}

export function ProjectDrawer({ project, users, canEdit, saving, onClose, onSave }: ProjectDrawerProps) {
  const [form, setForm] = useState<DrawerForm | null>(null);

  // Sync the editable form whenever the selected project changes, adjusting state
  // during render (via previous-value tracking) instead of in an effect.
  const [prevProject, setPrevProject] = useState(project);
  if (project !== prevProject) {
    setPrevProject(project);
    setForm(project ? toForm(project) : null);
  }

  if (!project || !form) return null;

  const set = <K extends keyof DrawerForm>(key: K, value: DrawerForm[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(project.id, {
      status: form.status,
      priority: form.priority,
      assigned_to: form.assigned_to,
      deadline: form.deadline || null,
      description: form.description.trim(),
      milestones: form.milestones.filter((m) => m.title.trim()),
      sprints: form.sprints.filter((s) => s.name.trim()),
    });
  };

  const pct = progressPct(project);
  const deadline = deadlineInfo(form.deadline);
  const assigneeName =
    project.assignee_name ??
    (typeof project.assigned_to === 'object' ? project.assignee_name : undefined);

  const assigneeOptions = users.map((u) => ({ value: u.id, label: u.display_name }));

  return (
    <Drawer open={!!project} onClose={onClose} title={project.name || 'Project Details'} width="w-[520px]">
      <form onSubmit={handleSave} className="flex h-full flex-col gap-5">
        {/* Header meta */}
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={PRIORITY_TONE[project.priority] ?? 'neutral'}>{project.priority}</Badge>
          <Badge tone={STATUS_TONE[project.status] ?? 'neutral'}>{STATUS_LABEL[project.status]}</Badge>
          <span className="rounded bg-surface-container px-2 py-0.5 text-[11px] text-on-surface-variant">
            {DEPT_LABELS[project.department] ?? project.department}
          </span>
        </div>

        {/* Progress — read-only */}
        <div>
          <div className="mb-1 flex justify-between text-[11px] text-on-surface-variant">
            <span>
              {project.done_count ?? 0}/{project.task_count ?? 0} tasks complete
            </span>
            <span>{pct}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-surface-container-highest">
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
          {deadline && (
            <div
              className={cn(
                'mt-2 flex items-center gap-1 text-[11px]',
                deadline.urgent ? 'text-danger' : 'text-on-surface-variant',
              )}
            >
              <CalendarDays className="h-3.5 w-3.5" />
              {deadline.label}
            </div>
          )}
        </div>

        {canEdit ? (
          <>
            {/* Status + Priority */}
            <div className="grid grid-cols-2 gap-3">
              <Select
                label="Status"
                name="drawer-project-status"
                value={form.status}
                onChange={(e) => set('status', e.target.value as ProjectStatus)}
                options={STATUS_OPTIONS}
              />
              <Select
                label="Priority"
                name="drawer-project-priority"
                value={form.priority}
                onChange={(e) => set('priority', e.target.value as ProjectPriority)}
                options={PRIORITY_OPTIONS}
              />
            </div>

            {/* Assigned User + Deadline */}
            <div className="grid grid-cols-2 gap-3">
              <MultiSelect
                label="Assigned Users"
                options={assigneeOptions}
                value={form.assigned_to}
                onChange={(val) => set('assigned_to', val)}
                placeholder="Select assignees..."
              />
              <Input
                label="Deadline"
                name="drawer-project-deadline"
                type="date"
                value={form.deadline}
                onChange={(e) => set('deadline', e.target.value)}
              />
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor="drawer-project-desc"
                className="mb-1.5 block text-body-sm font-medium text-on-surface"
              >
                Description
              </label>
              <textarea
                id="drawer-project-desc"
                rows={3}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                className="w-full resize-none rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          </>
        ) : (
          /* Read-only view for employees */
          <div className="space-y-3 text-data-md">
            <ReadOnlyRow label="Status" value={STATUS_LABEL[project.status]} />
            <ReadOnlyRow label="Priority" value={project.priority} />
            <ReadOnlyRow label="Assigned User" value={assigneeName ?? 'Unassigned'} />
            <ReadOnlyRow label="Deadline" value={project.deadline || '—'} />
            <div>
              <p className="mb-1 text-body-sm font-medium text-on-surface">Description</p>
              <p className="text-data-md text-on-surface-variant">{project.description || '—'}</p>
            </div>
          </div>
        )}

        {/* Milestones (spec §5) */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="flex items-center gap-1.5 text-body-sm font-medium text-on-surface">
              <Flag className="h-4 w-4 text-primary" aria-hidden />
              Milestones
            </p>
            {canEdit && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() =>
                  set('milestones', [...form.milestones, { id: localId(), title: '', due_date: '', done: false }])
                }
              >
                <Plus className="h-3.5 w-3.5" aria-hidden />
                Add
              </Button>
            )}
          </div>
          {form.milestones.length === 0 ? (
            <p className="text-body-sm text-on-surface-variant">No milestones yet.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {form.milestones.map((m, index) => (
                <li key={m.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    aria-label={`Milestone ${m.title || index + 1} done`}
                    checked={m.done}
                    disabled={!canEdit}
                    onChange={(e) =>
                      set(
                        'milestones',
                        form.milestones.map((x, i) => (i === index ? { ...x, done: e.target.checked } : x)),
                      )
                    }
                  />
                  {canEdit ? (
                    <>
                      <Input
                        aria-label={`Milestone ${index + 1} title`}
                        placeholder="Milestone title"
                        value={m.title}
                        onChange={(e) =>
                          set(
                            'milestones',
                            form.milestones.map((x, i) => (i === index ? { ...x, title: e.target.value } : x)),
                          )
                        }
                      />
                      <Input
                        aria-label={`Milestone ${index + 1} due date`}
                        type="date"
                        className="w-40"
                        value={m.due_date}
                        onChange={(e) =>
                          set(
                            'milestones',
                            form.milestones.map((x, i) => (i === index ? { ...x, due_date: e.target.value } : x)),
                          )
                        }
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label={`Remove milestone ${m.title || index + 1}`}
                        onClick={() => set('milestones', form.milestones.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </Button>
                    </>
                  ) : (
                    <span className={cn('text-data-md', m.done ? 'text-on-surface-variant line-through' : 'text-on-surface')}>
                      {m.title || '—'}
                      {m.due_date ? ` · due ${m.due_date}` : ''}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Sprints (spec §5) */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="flex items-center gap-1.5 text-body-sm font-medium text-on-surface">
              <Timer className="h-4 w-4 text-primary" aria-hidden />
              Sprints
            </p>
            {canEdit && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() =>
                  set('sprints', [...form.sprints, { id: localId(), name: '', start: '', end: '' }])
                }
              >
                <Plus className="h-3.5 w-3.5" aria-hidden />
                Add
              </Button>
            )}
          </div>
          {form.sprints.length === 0 ? (
            <p className="text-body-sm text-on-surface-variant">No sprints planned.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {form.sprints.map((s, index) => (
                <li key={s.id} className="flex items-center gap-2">
                  {canEdit ? (
                    <>
                      <Input
                        aria-label={`Sprint ${index + 1} name`}
                        placeholder="Sprint name"
                        value={s.name}
                        onChange={(e) =>
                          set('sprints', form.sprints.map((x, i) => (i === index ? { ...x, name: e.target.value } : x)))
                        }
                      />
                      <Input
                        aria-label={`Sprint ${index + 1} start`}
                        type="date"
                        className="w-36"
                        value={s.start}
                        onChange={(e) =>
                          set('sprints', form.sprints.map((x, i) => (i === index ? { ...x, start: e.target.value } : x)))
                        }
                      />
                      <Input
                        aria-label={`Sprint ${index + 1} end`}
                        type="date"
                        className="w-36"
                        value={s.end}
                        onChange={(e) =>
                          set('sprints', form.sprints.map((x, i) => (i === index ? { ...x, end: e.target.value } : x)))
                        }
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label={`Remove sprint ${s.name || index + 1}`}
                        onClick={() => set('sprints', form.sprints.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </Button>
                    </>
                  ) : (
                    <span className="text-data-md text-on-surface">
                      {s.name || '—'}
                      {s.start || s.end ? ` · ${s.start || '?'} → ${s.end || '?'}` : ''}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Project metadata */}
        <div className="grid grid-cols-2 gap-3 rounded-card-sm border border-outline bg-surface-variant p-3 text-[12px]">
          <Meta label="Project Value" value={formatValue(project.project_value)} />
          <Meta label="Created By" value={project.creator_name ?? '—'} />
        </div>

        {/* Footer */}
        <div className="mt-auto flex justify-end gap-3 border-t border-outline pt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Close
          </Button>
          {canEdit && (
            <Button type="submit" loading={saving}>
              Save Changes
            </Button>
          )}
        </div>
      </form>
    </Drawer>
  );
}

function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-outline pb-2">
      <span className="text-body-sm text-on-surface-variant">{label}</span>
      <span className="font-medium text-on-surface">{value}</span>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-on-surface-variant">{label}</p>
      <p className="font-semibold text-on-surface">{value}</p>
    </div>
  );
}
